import { useCallback } from 'react';
import { QueryKey, useQuery, useQueryClient, UseQueryOptions } from 'react-query';
import { useSelector } from 'react-redux';

import { DocumentsFiltersState } from '@app/features/documents/hooks/useDocumentsFilter';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';
import SigningMethodType from '@app/store/documents/SigningMethodType';
import Status from '@app/store/documents/Status';
import VisibleDocument from '@app/store/documents/VisibleDocument';
import { getDocuments } from '@app/store/transport-layer/document/documents';
import { getUserEmail } from '@app/store/user/selectors';

export function createQueryKey(workspaceId: string, filter?: DocumentsFiltersState): QueryKey {
  const key: unknown[] = ['documentList', workspaceId];
  if (filter) {
    key.push(filter);
  }
  return key;
}

export function useInvalidateDocumentList() {
  const queryClient = useQueryClient();

  return useCallback(() => queryClient.invalidateQueries(['documentList']), [queryClient]);
}

export default function useDocumentList(
  filter: DocumentsFiltersState,
  options?: UseQueryOptions<{ documents: VisibleDocument[]; maxPage: number; count: number }, Error>
) {
  const workspaceId = useWorkspaceId();
  const userEmail = useSelector(getUserEmail);

  const result = useQuery<{ documents: VisibleDocument[]; maxPage: number; count: number }, Error>({
    queryKey: createQueryKey(workspaceId, filter),
    queryFn: async () => {
      const result = await getDocuments(workspaceId, {
        ...filter,
        assigned: filter.assigned === 'SELF' ? true : undefined,
        sort: filter.sort ?? 'updatedAt:desc',
      });
      const maxPage = Math.ceil(result.countOfDocuments / (filter.limit ?? 10)) || 1;
      const documents: VisibleDocument[] = result.documents.map((d) => {
        return {
          ...d,
          attachmentsCount: d.attachmentsCount,
          status: d.status as Status,
          needAgreement: d.needAgreement,
          participants: d.participants.map((p) => {
            return {
              ...p,
              signingMethod: {
                contactInfo: p.signingMethod.contactInfo,
                methodType: p.signingMethod.methodType as SigningMethodType,
              },
            };
          }),
          file: d.file ?? '',
        };
      });

      return {
        documents,
        maxPage,
        count: result.countOfDocuments,
      };
    },
    keepPreviousData: true,
    enabled: !!userEmail && !!workspaceId,
    ...options,
  });

  return result;
}
